import { DynamicElementMixin } from "%/dynamic-components/render-engine/mixins/production/dynamic-element-mixin";
import { hasUnfilledPlaceholder } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-context-href";
import { ContextHrefMixin } from "%/dynamic-component-editor/standard-elements/click-actions/context-href-mixin";
import OptionExpression from "@bloglovin/inspire-engine/Helpers/OptionExpression.js";
import { resolveColor } from "@vcl";
import useDynamicComponentStore from "%/stores/dynamic-component-store";
import LayoutHeight from "%/dynamic-components/render-engine/options/layout-options/layout-height";
import LayoutWidth from "%/dynamic-components/render-engine/options/layout-options/layout-width";
import LayoutGap from "%/dynamic-components/render-engine/options/layout-options/layout-gap";
import normalizeGapValue from "%/dynamic-component-editor/helpers/gap-style-value";
import Padding from "%/dynamic-components/render-engine/options/spacing-options/padding";
import BorderRadius from "%/dynamic-components/render-engine/options/border-options/border-radius";
import LayoutDirection from "%/dynamic-components/render-engine/options/layout-options/layout-direction";
import BackgroundImage from "%/dynamic-components/render-engine/options/background-options/background-image";
import Overflow from "%/dynamic-components/render-engine/options/layout-options/overflow";
import MaxWidth from "%/dynamic-components/render-engine/options/spacing-options/max-width";
import BorderSides from "%/dynamic-components/render-engine/options/border-options/border-sides";
import BaseOption from "%/dynamic-components/render-engine/classes/base-option";
import Margins from "%/dynamic-components/render-engine/options/spacing-options/margins";
import MaxHeight from "%/dynamic-components/render-engine/options/spacing-options/max-height";
import MinWidth from "%/dynamic-components/render-engine/options/spacing-options/min-width";
import MinHeight from "%/dynamic-components/render-engine/options/spacing-options/min-height";
import { PADDING_DEFAULT_EMPTY_VALUE } from "%/dynamic-components/render-engine/options/spacing-options/padding";
import { MARGIN_DEFAULT_EMPTY_VALUE } from "%/dynamic-components/render-engine/options/spacing-options/margins";
import VerticalLayoutAlign from "%/dynamic-components/render-engine/options/layout-options/vertical-layout-align";
import HorizontalLayoutAlign from "%/dynamic-components/render-engine/options/layout-options/horizontal-layout-align";
import BackgroundColor from "%/dynamic-components/render-engine/options/background-options/background-color";
import VerticalLayoutVerticalAlign from "%/dynamic-components/render-engine/options/layout-options/vertical-layout-vertical-align";
import HorizontalLayoutVerticalAlign from "%/dynamic-components/render-engine/options/layout-options/horizontal-layout-vertical-align";
import LayoutWrap from "%/dynamic-components/render-engine/options/layout-options/layout-wrap";
import ScrunchWhenSqueezed from "%/dynamic-components/render-engine/options/layout-options/scrunch-when-squeezed";
import BackgroundPosition from "%/dynamic-components/render-engine/options/background-options/background-position";
import BackgroundSize from "%/dynamic-components/render-engine/options/background-options/background-size";
import BackgroundRepeat from "%/dynamic-components/render-engine/options/background-options/background-repeat";
import BackgroundGradient from "%/dynamic-components/render-engine/options/background-options/background-gradient";
import { composeBackgroundLayers } from "%/lib/compose-background-layers";
import BoxShadow from "%/dynamic-components/render-engine/options/box-shadow-options/box-shadow";
import ClickAction from "%/dynamic-components/render-engine/options/click-actions/click-action";
import LinkType from "%/dynamic-components/render-engine/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import DynamicComponentError from "%/errors/dynamic-component-error";
import {
  OptionMediaResolverMixin,
} from "%/dynamic-components/render-engine/mixins/production/option-media-resolver-mixin";
import { GestureActionsMixin } from "%/dynamic-component-editor/standard-elements/click-actions/gesture-actions-mixin";

const NO_BORDER_SIDE_VALUE = Object.freeze({ width: "0", color: "transparent", style: "solid" });
const NO_BORDER_SIDES = Object.freeze({
  top: NO_BORDER_SIDE_VALUE,
  right: NO_BORDER_SIDE_VALUE,
  bottom: NO_BORDER_SIDE_VALUE,
  left: NO_BORDER_SIDE_VALUE,
});

export const LayoutElementMixinProduction = {
  mixins: [ContextHrefMixin, DynamicElementMixin, OptionMediaResolverMixin, GestureActionsMixin],
  props: {
    configuration: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  options: [
    BorderSides,
    ClickAction,
  ],
  optionBundles: [
  ],
  inject: {
    parentLayoutAlign: { default: null },
  },
  provide() {
    return {
      parentLayoutAlign: () => this.resolvedLayoutAlignForChildren,
    };
  },
  computed: {
    myAlign() {
      return this.getOptionValue(
        this.layoutDirection === "vertical" ? VerticalLayoutAlign : HorizontalLayoutAlign,
      );
    },
    injectedParentLayoutAlign() {
      if (typeof this.parentLayoutAlign === "function") {
        return this.parentLayoutAlign();
      }
      return this.parentLayoutAlign;
    },
    resolvedLayoutAlignForChildren() {
      if (this.myAlign !== undefined && this.myAlign !== null) {
        return this.myAlign;
      }
      return this.injectedParentLayoutAlign;
    },
    myLayoutProps() {
      const padding = this.myPadding();
      const margins = this.myMargins();
      return {
        align: this.myAlign,
        overflow: this.myOverflow,
        gap: this.myGap,
        height: this.myHeight,
        maxWidth: this.myMaxWidth,
        minWidth: this.myMinWidth,
        maxHeight: this.myMaxHeight,
        minHeight: this.myMinHeight,
        styleOverrides: {
          paddingTop: padding.top,
          paddingRight: padding.right,
          paddingBottom: padding.bottom,
          paddingLeft: padding.left,
          marginTop: margins.top,
          marginRight: margins.right,
          marginBottom: margins.bottom,
          marginLeft: margins.left,
          borderRadius: this.myBorderRadius,
          backgroundColor: this.getOptionValue(BackgroundColor),
          hoverBackgroundColor: this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER),
          backgroundImage: composeBackgroundLayers(
            this.getOptionValue(BackgroundGradient),
            this.myBackgroundImage,
          ),
          backgroundImageHover: composeBackgroundLayers(
            this.getOptionValue(BackgroundGradient, BaseOption.$states.HOVER),
            this.myBackgroundImageHover,
          ),
          boxShadow: this.getOptionValue(BoxShadow),
          boxShadowHover: this.getOptionValue(BoxShadow, BaseOption.$states.HOVER),
        },
        borderTop: this.myBorderSides.top,
        borderBottom: this.myBorderSides.bottom,
        borderLeft: this.myBorderSides.left,
        borderRight: this.myBorderSides.right,
        progressRing: this.myProgressRing,
        verticalAlign: this.getOptionValue(this.layoutDirection === "vertical" ? VerticalLayoutVerticalAlign : HorizontalLayoutVerticalAlign),
        width: this.myWidth,
        wrap: this.getOptionValue(LayoutWrap),
        noShrink: this.getOptionValue(ScrunchWhenSqueezed) === "no",
        backgroundPosition: this.getOptionValue(BackgroundPosition),
        backgroundSize: this.getOptionValue(BackgroundSize),
        backgroundRepeat: this.getOptionValue(BackgroundRepeat),
        hoverBackgroundPosition: this.getOptionValue(BackgroundPosition, BaseOption.$states.HOVER),
        hoverBackgroundSize: this.getOptionValue(BackgroundSize, BaseOption.$states.HOVER),
        hoverBackgroundRepeat: this.getOptionValue(BackgroundRepeat, BaseOption.$states.HOVER),
        href: this.editMode || this.gestureOwnsTheClick ? null : this.clickValue,
      };
    },
    myBackgroundImage() {
      const source = this.resolveOptionMedia(this.getOptionValue(BackgroundImage));

      if (!source) {
        return "";
      }

      return `url(${source})`;
    },
    myBackgroundImageHover() {
      const source = this.resolveOptionMedia(this.getOptionValue(BackgroundImage, BaseOption.$states.HOVER));

      if (!source) {
        return "";
      }

      return `url(${source})`;
    },
    layoutDirection() {
      return this.getOptionValue(LayoutDirection);
    },
    myHeight() {
      const height = this.getOptionValue(LayoutHeight);
      return height === "fill" ? "fillv2" : height;
    },
    myOverflow() {
      return this.getOptionValue(Overflow);
    },
    myWidth() {
      return this.getOptionValue(LayoutWidth);
    },
    myGap() {
      return normalizeGapValue(this.getOptionValue(LayoutGap));
    },
    myBorderRadius() {
      return this.getOptionValue(BorderRadius);
    },
    myMaxWidth() {
      return this.getOptionValue(MaxWidth);
    },
    myMaxHeight() {
      return this.getOptionValue(MaxHeight);
    },
    myMinWidth() {
      return this.getOptionValue(MinWidth);
    },
    myMinHeight() {
      return this.getOptionValue(MinHeight);
    },
    myCompiledBorder() {
      return this.getOptionValue(BorderSides);
    },
    isClockBorder() {
      return this.myCompiledBorder?.mode === "clock";
    },
    myBorderSides() {
      return this.isClockBorder ? NO_BORDER_SIDES : this.myCompiledBorder;
    },
    myProgressRing() {
      if (!this.isClockBorder) {
        return null;
      }
      const border = this.myCompiledBorder;
      return {
        percent: this.resolveClockPercent(border.percent),
        thickness: border.thickness,
        completeColor: this.ringColor(border.completeColor),
        incompleteColor: this.ringColor(border.incompleteColor),
      };
    },
    hasClickValue() {
      if (this.editMode) {
        return false;
      }

      return this.clickValue !== null && this.clickValue?.length > 0;
    },
    clickValue() {
      let urlValue = this.getOptionValue(ClickAction)?.value;

      if (!this.linkType || urlValue === null || urlValue === undefined) {
        return;
      }

      switch (this.linkType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue;
        case LinkType.LINK_TYPE_PAGE: {
          const contextHref = this.contextResolvedHref(
            this.getOptionValue(ClickAction), urlValue, this.links.web,
          );
          if (contextHref) {
            return contextHref;
          }
          urlValue = resolveContextualPageValue(urlValue);
          if (urlValue === null || urlValue === undefined) {
            return;
          }
          if (urlValue.startsWith("/")) {
            urlValue = urlValue.substring(1);
          }
          const pageHref = `${this.links.web}/${urlValue}`;
          return hasUnfilledPlaceholder(pageHref) ? undefined : pageHref;
        }
        case LinkType.LINK_TYPE_STORY:
          return `${this.links.web}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${this.links.web}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            linkType: this.linkType,
            urlValue,
          });
      }
    },
    linkType() {
      return this.getOptionValue(ClickAction)?.linkType;
    },
    actionType() {
      return this.getOptionValue(ClickAction)?.action;
    },
    openInNewTab() {
      return this.getOptionValue(ClickAction)?.openInNewTab;
    },
  },
  methods: {
    ringColor(color) {
      if (typeof color === "string") {
        return color;
      }
      const resolved = resolveColor(color, { themeId: useDynamicComponentStore().themeId, themeMode: "inherit" });
      return typeof resolved === "string" ? resolved : "transparent";
    },
    resolveClockPercent(percent) {
      let value = percent;
      if (OptionExpression.isToken(percent)) {
        const scope = typeof this.$options.methods?.resolveContextBindingScope === "function"
          ? this.resolveContextBindingScope()
          : { contexts: {} };
        value = OptionExpression.resolve(percent, scope);
      }
      if (value !== null && typeof value === "object") {
        return 0;
      }
      const n = Number(value);
      return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
    },
    myPadding(state = BaseOption.$states.DEFAULT) {
      let paddingValues = this.getOptionValue(Padding, state, null);
      const paddingKeys = Object.keys(paddingValues);
      paddingKeys.forEach(paddingKey => {
        const paddingVal = paddingValues[paddingKey];
        if (paddingVal === null) {
          paddingValues[paddingKey] = PADDING_DEFAULT_EMPTY_VALUE;
        }
      });
      return paddingValues;
    },
    myMargins(state = BaseOption.$states.DEFAULT) {
      let marginValues = this.getOptionValue(Margins, state, null);
      const marginKeys = Object.keys(marginValues);
      marginKeys.forEach(marginKey => {
        const marginVal = marginValues[marginKey];
        if (marginVal === null) {
          marginValues[marginKey] = MARGIN_DEFAULT_EMPTY_VALUE;
        }
      });
      return marginValues;
    },
  },
};
